import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useInstanceStateStore } from "@/store/useInstanceStateStore";

// Phase 11b — Universal Day-1 empty state component.
// Renders only when mode === 'fresh'. In demo mode, returns null so caller
// continues to seeded UI unchanged.
//
// Usage:
//   const fresh = useFreshMode();
//   if (fresh) return <Day1Empty Icon={MessageSquare} title="..." body="..." actions={[...]} />;
//   ...existing seeded UI...

export function useFreshMode() {
  return useInstanceStateStore((s) => s.mode === "fresh");
}

export function Day1Empty({ Icon, title, body, actions = [], className }) {
  return (
    <div className={cn("min-h-[50vh] flex items-center justify-center px-6 py-12", className)} data-testid="day1-empty">
      <div className="max-w-[480px] text-center">
        {Icon && (
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 ring-1 ring-indigo-100 text-indigo-600">
            <Icon size={22} strokeWidth={1.75} />
          </div>
        )}
        <h2 className="mt-4 text-[20px] font-semibold tracking-tight text-slate-900 leading-snug">{title}</h2>
        <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{body}</p>
        {actions.length > 0 && (
          <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
            {actions.map((a, i) => {
              const variant = i === 0 ? "default" : "outline";
              const inner = <>{a.label} {a.suffix || null}</>;
              if (a.to) return <Link key={a.label} to={a.to}><Button size="sm" variant={variant} className="h-9 text-[12px]" data-testid={a.testId}>{inner}</Button></Link>;
              return <Button key={a.label} size="sm" variant={variant} className="h-9 text-[12px]" onClick={a.onClick} data-testid={a.testId}>{inner}</Button>;
            })}
          </div>
        )}
      </div>
    </div>
  );
}

Day1Empty.propTypes = {
  Icon: PropTypes.elementType,
  title: PropTypes.string,
  body: PropTypes.string,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      suffix: PropTypes.node,
      to: PropTypes.string,
      onClick: PropTypes.func,
      testId: PropTypes.string,
    })
  ),
  className: PropTypes.string,
};
